import { h, ref, watch } from 'vue'
import type { Column, SortingState } from '@tanstack/vue-table'
// Direct subpath imports — the @nuxt/ui Vite plugin only auto-registers
// components for .vue files, so resolveComponent('UButton') from this .ts
// composable would return the unresolved string. The package's public
// `./components/*` exports map (→ ./dist/runtime/components/*) lets us
// import the component directly.
//
// NOTE: do NOT import Icon.vue this way. Icon.vue's template uses a
// globally-registered <Icon> tag that the @nuxt/ui plugin only wires up
// for .vue consumers; calling h(UIcon) from a .ts file lets that <Icon>
// resolve recursively to UIcon itself, blowing the call stack. Lean on
// UButton's own `icon` prop instead — UButton's compiled template has
// the resolution baked in at install time, so iconify icons render fine.
import UButton from '@nuxt/ui/components/Button.vue'
import UDropdownMenu from '@nuxt/ui/components/DropdownMenu.vue'

interface UseTableSortingOptions {
  initialSort?: SortingState
  /**
   * Stable identity for this table. When set, the sort state is cached in a
   * module-level (in-memory) store keyed by this string, so it survives
   * client-side navigation — leaving and re-entering the view restores the
   * last sort. A full page reload clears the store, falling back to
   * `initialSort`.
   */
  key?: string
}

// Module-scoped on purpose: outlives component unmount, dies on full reload.
const sortingStore = new Map<string, SortingState>()

const SORT_ICON = {
  asc: 'i-lucide-arrow-up-narrow-wide',
  desc: 'i-lucide-arrow-down-wide-narrow',
  none: 'i-lucide-arrow-up-down',
} as const

/**
 * Multi-column sorting helper for UTable.
 *
 * Click behavior: tapping a header opens a dropdown menu with explicit
 * Ascending / Descending / No sort options. Picking a direction for a column
 * already in the multi-sort flips it in place — the column's priority slot is
 * preserved, which the previous cycle-through-states gesture could not offer
 * (cycling past "off" always dropped the column from the sort list). When
 * more than one column is sorted, a small subscript-style priority badge is
 * anchored to the bottom-left of the sort icon, so the number reads as a
 * sort-priority annotation without pushing the column label to the side.
 *
 * Styles for the priority badge are global, in `src/style.css`
 * (`.sort-trigger`, `.sort-priority`).
 */
export function useTableSorting(options: UseTableSortingOptions = {}) {
  const { key, initialSort } = options
  const cached = key ? sortingStore.get(key) : undefined
  const sorting = ref<SortingState>(cached ?? initialSort ?? [])

  // Persist every change to the module store so the next mount restores it.
  if (key) {
    watch(sorting, (value) => {
      sortingStore.set(key, value)
    })
  }

  function resetSort() {
    sorting.value = []
  }

  // Explicit direction setter. We mutate `sorting` directly instead of
  // calling `column.toggleSorting(desc, true)` because tanstack's toggle
  // treats a manual direction matching the current direction as "remove" —
  // which would make "pick Ascending on an already-asc column" silently
  // drop the column from the sort. Direct mutation also lets us preserve
  // the column's array index (== priority) when only flipping direction.
  function setSort(columnId: string, dir: 'asc' | 'desc' | false) {
    const current = sorting.value
    const idx = current.findIndex((s) => s.id === columnId)

    if (dir === false) {
      if (idx >= 0) sorting.value = current.filter((s) => s.id !== columnId)
      return
    }

    const desc = dir === 'desc'
    if (idx >= 0) {
      sorting.value = current.map((s, i) => (i === idx ? { ...s, desc } : s))
    } else {
      sorting.value = [...current, { id: columnId, desc }]
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getHeader(column: Column<any>, label: string) {
    const isSorted = column.getIsSorted()
    const sortIndex = column.getSortIndex()
    const showPriority = isSorted !== false && sorting.value.length > 1

    const items = [
      {
        label: 'Ascending',
        icon: SORT_ICON.asc,
        type: 'checkbox' as const,
        checked: isSorted === 'asc',
        onSelect: () => setSort(column.id, 'asc'),
      },
      {
        label: 'Descending',
        icon: SORT_ICON.desc,
        type: 'checkbox' as const,
        checked: isSorted === 'desc',
        onSelect: () => setSort(column.id, 'desc'),
      },
      { type: 'separator' as const },
      {
        label: 'No sort',
        icon: SORT_ICON.none,
        type: 'checkbox' as const,
        checked: isSorted === false,
        onSelect: () => setSort(column.id, false),
      },
    ]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return h(UDropdownMenu as any, { items }, {
      // DropdownMenuTrigger wraps this slot with `as-child`, so click and
      // keyboard handling are merged onto the UButton — no onClick needed.
      default: () =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        h(UButton as any, {
          color: 'neutral',
          variant: 'ghost',
          icon: SORT_ICON[isSorted || 'none'],
          label,
          // `sort-trigger` sets position:relative so the absolutely-positioned
          // priority badge below anchors against the button's box.
          class: '-mx-2.5 sort-trigger',
        }, {
          // The trailing slot is rendered as a direct child of UButton's base
          // element. With `position: absolute` the badge is taken out of flow,
          // so it doesn't push the layout — it just floats at the bottom-left
          // corner of the sort icon as a subscript annotation.
          trailing: () => showPriority
            ? h('span', { class: 'sort-priority' }, String(sortIndex + 1))
            : null,
        }),
    })
  }

  return { sorting, getHeader, resetSort }
}
